import { Component, computed, effect, inject, input, signal, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { EntreeInventoryFilterV1, toStockDateParam } from '@producers/entree-inventory/entree-inventory';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { DatePickerModule } from 'primeng/datepicker';
import { MessageModule } from 'primeng/message';
import { SelectModule } from 'primeng/select';
import { SkeletonModule } from 'primeng/skeleton';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';

import { EntreeInventoryService } from '@core/services/entree-inventory';

/** An establishment that offers the entree, reduced to what the picker needs. */
export interface EstablishmentOption {
  id: string;
  name: string;
}

/** One calendar day in the window. `null` numbers mean the establishment holds no row for the day. */
export interface MenuItemInventoryRow {
  stockDate: string;
  isSelected: boolean;
  hasRow: boolean;
  stock: number | null;
  held: number | null;
  committed: number | null;
  capacity: number | null;
}

/** Days shown on each side of the selected day. A 7-day window, well inside the engine's 60-day cap. */
const WINDOW_RADIUS = 3;

/** Add whole days using local calendar fields, so the date never shifts through UTC. */
function addDays(date: Date, days: number): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
}

@Component({
  selector: 'app-menu-item-inventory',
  standalone: true,
  imports: [
    FormsModule,
    ButtonModule,
    CardModule,
    DatePickerModule,
    MessageModule,
    SelectModule,
    SkeletonModule,
    TableModule,
    TagModule
  ],
  templateUrl: './menu-item-inventory.html',
  styleUrl: './menu-item-inventory.scss'
})
export class MenuItemInventory {
  private readonly inventoryService = inject(EntreeInventoryService);

  readonly entreeId = input.required<string>();
  readonly establishments = input<EstablishmentOption[]>([]);

  readonly selectedEstablishmentId = signal<string | null>(null);
  readonly selectedDate = signal<Date>(new Date());

  readonly filter = computed<EntreeInventoryFilterV1 | null>(() => {
    const establishmentId = this.selectedEstablishmentId();
    if (!establishmentId) return null;

    const selected = this.selectedDate();
    return {
      establishmentId,
      entreeId: this.entreeId(),
      fromDate: toStockDateParam(addDays(selected, -WINDOW_RADIUS)),
      toDate: toStockDateParam(addDays(selected, WINDOW_RADIUS))
    };
  });

  readonly inventoryResource = this.inventoryService.createInventoryResource(this.filter);

  readonly hasEstablishments = computed(() => this.establishments().length > 0);
  readonly isLoading = computed(() => this.inventoryResource.isLoading());
  readonly loadFailed = computed(() => this.inventoryResource.error() !== undefined);

  /**
   * The window's 7 calendar days, each left-joined onto the rows the API returned. Every day appears:
   * a day the establishment holds no row for renders with `null` numbers ("no row"), which is a
   * different fact from a day whose `stock` is 0 (sold out).
   */
  readonly rows = computed<MenuItemInventoryRow[]>(() => {
    const selected = this.selectedDate();
    const selectedParam = toStockDateParam(selected);
    const inventory = this.inventoryResource.hasValue() ? this.inventoryResource.value() : [];
    const byDate = new Map(inventory.map((row) => [row.stockDate, row]));

    const days: MenuItemInventoryRow[] = [];
    for (let offset = -WINDOW_RADIUS; offset <= WINDOW_RADIUS; offset++) {
      const stockDate = toStockDateParam(addDays(selected, offset));
      const row = byDate.get(stockDate);
      days.push({
        stockDate,
        isSelected: stockDate === selectedParam,
        hasRow: row !== undefined,
        stock: row?.stock ?? null,
        held: row?.held ?? null,
        committed: row?.committed ?? null,
        capacity: row === undefined ? null : row.stock + row.held + row.committed
      });
    }
    return days;
  });

  constructor() {
    // Default to the first establishment, and re-default whenever the list changes to one that no
    // longer contains the current selection (e.g. navigating between menu items).
    effect(() => {
      const list = this.establishments();
      untracked(() => {
        const current = this.selectedEstablishmentId();
        if (!current || !list.some((e) => e.id === current)) {
          this.selectedEstablishmentId.set(list[0]?.id ?? null);
        }
      });
    });
  }
}
